import { Dispatch, useState } from 'react';
import {
  Stack,
  Checkbox,
  FileInput,
  Tooltip,
  Icon,
  SelectGroup
} from '@efast_public/fjd-component-library';
import UploadsList from '../../pages/shared/UploadsList';
import {
  FileUploadAction,
  ActionType
} from '../../api/models/UploadFiles/FileUploadAction';
import { UploadFiles } from '../../api/models/UploadFiles/UploadFiles';

interface DecideApplicationPostalProps {
  readonly isApplicationExaminedAndChecked: boolean;
  readonly setApplicationIsPositiveDecided: (
    isPositiveDecided: boolean | undefined
  ) => void;
  readonly fileDispatch: Dispatch<FileUploadAction>;
  readonly filesUploadstate: UploadFiles;
  readonly applicationIsPositiveDecided: boolean | undefined;
  readonly isApplicationFinished: boolean;
  readonly setApplicationIsFinished: (applicationIsFinished: boolean) => void;
}

export default function DecideApplicationPostal(
  props: DecideApplicationPostalProps
) {
  const filesUploadstate: UploadFiles = props.filesUploadstate;

  const [sendPostallyConfirmed, setSendPostallyConfirmed] = useState<boolean>(
    (filesUploadstate.bescheidUpload !== undefined ||
      filesUploadstate.additionalFiles.length > 0) &&
      props.isApplicationExaminedAndChecked
  );

  const isDisabledAndUnsetForSendDigitallyCheck: () => boolean = () => {
    if (props.isApplicationExaminedAndChecked) {
      return false;
    } else {
      if (sendPostallyConfirmed) {
        setSendPostallyConfirmed(false);
      }
      return true;
    }
  };

  const isDisabledAndUnsetForApplictionIsFinishedCheck: () => boolean = () => {
    if (sendPostallyConfirmed) {
      return false;
    } else {
      if (props.isApplicationFinished) {
        props.setApplicationIsFinished(false);
      }
      return true;
    }
  };

  return (
    <Stack direction={'column'} spacing="3xl">
      <Stack direction={'column'} spacing="sm">
        <div
          style={{
            color: props.isApplicationExaminedAndChecked
              ? '#333333'
              : '#999999',
            fontWeight: 700,
            fontSize: '0.97rem'
          }}
          id={'send_postally_confirmed_heading'}
        >
          Bitte bestätigen Sie, dass der Bescheid postalisch zugestellt wird
        </div>
        <Stack direction={'column'} spacing={'xl'}>
          <Stack direction={'row'} spacing={'sm'}>
            <Checkbox
              id={'send_postally_confirmed_checkbox'}
              label={'Der Bescheid wird postalisch zugestellt'}
              onChange={() => {
                if (sendPostallyConfirmed) {
                  props.fileDispatch({
                    type: ActionType.REMOVE_ALL_FILES,
                    onConfirmation: () => {
                      setSendPostallyConfirmed(!sendPostallyConfirmed);
                      props.setApplicationIsPositiveDecided(undefined);
                    }
                  });
                } else {
                  setSendPostallyConfirmed(!sendPostallyConfirmed);
                  props.setApplicationIsPositiveDecided(undefined);
                }
              }}
              disabled={isDisabledAndUnsetForSendDigitallyCheck()}
              checked={
                props.isApplicationExaminedAndChecked && sendPostallyConfirmed
              }
            />
          </Stack>
        </Stack>
      </Stack>

      <Stack direction="column">
        <div
          style={{
            color: sendPostallyConfirmed ? '#333333' : '#999999',
            fontWeight: 700,
            fontSize: '0.97rem'
          }}
          id={'send_postally_bescheid_heading'}
        >
          Geben Sie bitte an, ob der Bescheid positiv oder negativ ist
        </div>
        <SelectGroup
          id="btn-select-group-bescheid-postal"
          disabled={!sendPostallyConfirmed}
          name="result"
          selectType="single"
        >
          <SelectGroup.Button
            checked={props.applicationIsPositiveDecided === true}
            value="positive"
            label={"✓ Positiv"}
            onClick={() => {
              props.setApplicationIsPositiveDecided(
                props.applicationIsPositiveDecided === true ? undefined : true
              );
            }}
          />
          <SelectGroup.Button
            checked={props.applicationIsPositiveDecided === false}
            value="negative"
            label={"✗ Negativ"}
            onClick={() => {
              props.setApplicationIsPositiveDecided(
                props.applicationIsPositiveDecided === false ? undefined : false
              );
            }}
          />

        </SelectGroup>
      </Stack>

      <Stack direction={'column'} spacing={'sm'}>
        <div>
          <Stack direction={'row'} spacing={'xs'}>
            <span
              style={{
                color: sendPostallyConfirmed ? '#333333' : '#999999',
                fontWeight: 700,
                fontSize: '0.97rem'
              }}
              id={'upload_heading'}
            >
              Bescheidupload{' '}
            </span>
            <span
              style={{
                color: sendPostallyConfirmed ? '#333333' : '#999999',
                fontSize: '0.97rem'
              }}
            >
              (optional)
            </span>
            <Tooltip
              placement="auto"
              tooltip={
                <div>
                  Sie können optional den Bescheid hochladen, um diesen im
                  System zu hinterlegen.
                </div>
              }
            >
              <span>
                <Icon variant="info" name="information-filled" />
              </span>
            </Tooltip>
          </Stack>
        </div>
        {!filesUploadstate.bescheidUpload ? (
          <FileInput
            disabled={!sendPostallyConfirmed}
            id="application_upload_button"
            label="Bescheid hochladen"
            name="application_upload_button"
            onChange={(files) => {
              props.fileDispatch({
                type: ActionType.ADD_BESCHEID,
                files: files ?? []
              });
            }}
          />
        ) : (
          <UploadsList
            applicationIsPositiveDecided={props.applicationIsPositiveDecided}
            setApplicationIsPositiveDecided={
              props.setApplicationIsPositiveDecided
            }
            uploadFiles={filesUploadstate}
            hasBescheidSelect={false}
            dispatch={props.fileDispatch}
            hasUploadButtonEnabled={true}
            uploadButtonTextAtLeastOneFileUploaded={
              'Weiteres Dokument hinzufügen'
            }
            showPreview={true}
            hasIconsRightAligned={true}
          />
        )}
      </Stack>
      <Stack direction={'column'} spacing={'sm'}>
        <div>
          <Stack direction={'row'} spacing={'xs'}>
            <span
              style={{
                color: sendPostallyConfirmed ? '#333333' : '#999999',
                fontWeight: 700,
                fontSize: '0.97rem'
              }}
              id={'finish_application_heading'}
            >
              Antrag abschließen?{' '}
            </span>
            <span
              style={{
                color: sendPostallyConfirmed ? '#333333' : '#999999',
                fontSize: '0.97rem'
              }}
            >
              (optional)
            </span>
            <Tooltip
              placement="auto"
              tooltip={
                <div>
                  Nach dem Abschließen des Antrags wird dieser nach Ablauf von
                  30 Tagen aus dem Behörden-Client gelöscht.
                </div>
              }
            >
              <span>
                <Icon variant="info" name="information-filled" />
              </span>
            </Tooltip>
          </Stack>
        </div>
        <Stack direction={'row'} spacing={'sm'}>
          <Checkbox
            id={'finish_application_checkbox'}
            label={
              'Den Antrag direkt als abgeschlossen kennzeichnen (Downloadoption im nächsten Schritt)'
            }
            onChange={() =>
              props.setApplicationIsFinished(!props.isApplicationFinished)
            }
            disabled={isDisabledAndUnsetForApplictionIsFinishedCheck()}
            checked={props.isApplicationFinished}
          />
        </Stack>
      </Stack>
    </Stack>
  );
}
